import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ApartmentIcon from "@mui/icons-material/Apartment";
import allApi from "../../../api/allApi";
import { useNetwork } from "../../component/common/networkContext";
import { useLocalization } from "../localization/LocalizationContext";

const TreeNode = ({ node, level = 0, onNodeSelect, selectedId, isLast = false, isDarkMode }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = node.children?.length > 0;
  const isSelected = selectedId === node.id;

  const getIcon = () => {
    const type = node.type?.toLowerCase() || "";
    if (type === "root") return <ApartmentIcon fontSize="small" sx={{ mr: 0.6 }} />;
    if (type === "org") return <BusinessIcon fontSize="small" sx={{ mr: 0.6 }} />;
    if (type === "loc") return <LocationOnIcon fontSize="small" sx={{ mr: 0.6 }} />;
    return null;
  };

  return (
    <Box sx={{ position: "relative", mb: 0.2 }}>
      {/* Vertical connector line */}
      {level > 0 && (
        <Box
          sx={{
            position: "absolute",
            left: `${level * 18 - 10}px`,
            top: 0,
            bottom: 0,
            width: "1px",
              bgcolor: isDarkMode ? "#334155" : "grey.400",
              opacity: isDarkMode ? 0.85 : 0.9,
          }}
        />
      )}

      {/* Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pl: `${level * 20}px`,
          gap: 1,
          minHeight: "28px",
          position: "relative",
        }}
      >
        {/* Branch connector */}
        {level > 0 && (
          <Typography
            sx={{
              fontSize: "12px",
              color: isDarkMode ? "#64748B" : "grey.500",
              width: "20px",
              userSelect: "none",
            }}
          >
            {isLast ? "└─" : "├─"}
          </Typography>
        )}

        {/* Expand / collapse toggle */}
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={() => setExpanded((p) => !p)}
            sx={{ p: 0.2, color: isDarkMode ? "#94A3B8" : "inherit" }}
          >
            {expanded ? <Remove fontSize="small" /> : <Add fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 22 }} />
        )}

        {/* Label */}
        <Typography
          onClick={() => onNodeSelect(node)}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            bgcolor: isSelected ? (isDarkMode ? "#0E7490" : "#E0F2FE") : "transparent",
            borderRadius: 1,
            fontFamily: "inherit",
            px: 1,
            py: 0.45,
            lineHeight: 1.25,
            fontSize: node.type === "loc" ? "11px" : "12px",
            color: isDarkMode ? "#E2E8F0" : "inherit",
            "&:hover": { bgcolor: isDarkMode ? "#1E293B" : "grey.100" },
          }}
        >
          {getIcon()}
          {node.label}
          {node.children?.length > 0 && node.type !== "root" && ` (${node.children.length})`}
        </Typography>
      </Box>

      {/* Children */}
      {hasChildren && expanded && (
        <Box sx={{ mt: 0.5 }}>
          {node.children.map((child, index) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onNodeSelect={onNodeSelect}
              selectedId={selectedId}
              isLast={index === node.children.length - 1}
              isDarkMode={isDarkMode}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default function CustomHierarchyDropdown({ onSelect, initialOpen = false, popupMode = false }) {
  const { setSelectedNetworkId, setSelectedLocation } = useNetwork();
  const isDarkMode = true;
  const { localizationStrings } = useLocalization();
  const [open, setOpen] = useState(popupMode ? true : initialOpen);
  const [search, setSearch] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [treeData, setTreeData] = useState([]);

  useEffect(() => {
    setOpen(popupMode ? true : initialOpen);
  }, [initialOpen, popupMode]);

  useEffect(() => {
    const fetchNetworks = async () => {
      try {
        const api = new allApi();
        const response = await api.getRequest("api/network/getAssociatedNetwork");

        if (response?.status === "OK" && Array.isArray(response.response)) {
          const formattedTree = response.response.map((network) => ({
            id: network.network_id,
            label: network.network_name || "Network",
            type: "org",
            country: network.country,
            location: network.network_location,
            timezone: network.network_timezone,
            children: [],
          }));
          setTreeData(formattedTree);
        } else {
          setTreeData([]);
        }
      } catch (error) {
        console.error("Failed to fetch networks:", error);
        setTreeData([]);
      }
    };

    fetchNetworks();
  }, []);

  useEffect(() => {
    try {
      const savedNode = localStorage.getItem("selectedHierarchyNode");

      if (savedNode) {
        setSelectedNode(JSON.parse(savedNode));
      }
    } catch (error) {
      console.error("Failed to restore hierarchy selection", error);
    }
  }, []);

  const normalizedTree = [
    {
      id: "root-all-orgs",
      label: localizationStrings.allLocations,
      type: "root",
      children: treeData,
    },
  ];

  const filterTree = (nodes, query) => {
    if (!query) return nodes;
    return nodes
      .map((node) => {
        if (node.label.toLowerCase().includes(query.toLowerCase())) return node;
        if (node.children?.length) {
          const filtered = filterTree(node.children, query);
          if (filtered.length) return { ...node, children: filtered };
        }
        return null;
      })
      .filter(Boolean);
  };

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
    setOpen(false);
    setSearch("");

    if (node.type === "root") {
      setSelectedNetworkId(null);
      setSelectedLocation(null);

      localStorage.removeItem("selectedHierarchyNode");

    } else if (node.type === "org") {
      setSelectedNetworkId({
        id: node.id,
        name: node.label,
      });

      setSelectedLocation(null);

      localStorage.setItem("selectedHierarchyNode", JSON.stringify(node));

    } else if (node.type === "loc") {
      setSelectedNetworkId({
        id: node.networkId,
        name: "",
      });

      setSelectedLocation({
        id: node.id,
        name: node.label,
      });

      localStorage.setItem("selectedHierarchyNode", JSON.stringify(node));
    }

    onSelect?.(node);
  };

  return (
    <Box sx={{ position: "relative", width: "100%", mt: 0 }}>
      {!popupMode && (
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 650,
            letterSpacing: 0.12,
            textTransform: "uppercase",
            color: "#64748B",
            mb: 0.75,
            px: "2px",
            fontFamily: "inherit",
          }}
        >
          {localizationStrings.hierarchyTree}
        </Typography>
      )}

      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{
          border: "1px solid rgba(148, 163, 184, 0.18)",
          borderRadius: "8px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(15, 23, 42, 0.55)",
          mx: 0,
          padding: "8px 10px",
          transition: "0.15s",
          "&:hover": {
            backgroundColor: "rgba(30, 41, 59, 0.9)",
            borderColor: "rgba(56, 189, 248, 0.45)",
            boxShadow: "none",
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontFamily: "inherit",
            ml: "-2px",
            color: isDarkMode ? "#E2E8F0" : "inherit",
          }}
        >
          {selectedNode?.label ? (
            <>
              {selectedNode.type === "org" && <BusinessIcon sx={{ fontSize: 16, color: isDarkMode ? "#94A3B8" : "text.secondary" }} />}
              {selectedNode.type === "loc" && <LocationOnIcon sx={{ fontSize: 16, color: isDarkMode ? "#94A3B8" : "text.secondary" }} />}
              {selectedNode.type === "root" && <ApartmentIcon sx={{ fontSize: 16, color: isDarkMode ? "#94A3B8" : "text.secondary" }} />}
              {selectedNode.label}
            </>
          ) : (
            <>
              <ApartmentIcon sx={{ fontSize: 18, color: isDarkMode ? "#94A3B8" : "text.secondary" }} />
              {localizationStrings.allLocations}
            </>
          )}
        </Typography>

        {open
          ? <ExpandLessIcon sx={{ fontSize: 20, color: isDarkMode ? "#94A3B8" : "text.secondary" }} />
          : <ExpandMoreIcon sx={{ fontSize: 20, color: isDarkMode ? "#94A3B8" : "text.secondary" }} />
        }
      </Box>

      {/* Dropdown panel */}
      {open && (
        <ClickAwayListener onClickAway={() => { setOpen(false); setSearch(""); }}>
          <Paper
            sx={{
              position: "absolute",
              top: "calc(100% + 12px)",
              left: "2px",
              width: 280,
              maxWidth: "100vw",
              maxHeight: 420,
              overflowY: "auto",
              borderRadius: "10px",
              boxShadow: isDarkMode
                ? "0 10px 28px rgba(0, 0, 0, 0.45)"
                : "0 8px 24px rgba(15, 23, 42, 0.08)",
              border: isDarkMode ? "1px solid #1E293B" : "1px solid #E2E8F0",
              p: 1.5,
              backgroundColor: isDarkMode ? "#0F172A" : "background.paper",
              zIndex: 2000,
            }}
          >
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              autoFocus
              variant="outlined"
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: isDarkMode ? "#111827" : "transparent",
                  color: isDarkMode ? "#FFFFFF" : "inherit",
                  "& fieldset": { borderColor: isDarkMode ? "#334155" : undefined },
                  "&:hover fieldset": { borderColor: isDarkMode ? "#38BDF8" : undefined },
                  "&.Mui-focused fieldset": { borderColor: isDarkMode ? "#38BDF8" : undefined },
                },
                "& .MuiInputBase-input": { color: isDarkMode ? "#FFFFFF" : "inherit" },
                "& .MuiInputBase-input::placeholder": {
                  color: isDarkMode ? "#64748B" : undefined,
                  opacity: 1,
                },
              }}
            />

            {/* Tree */}
            {filterTree(normalizedTree, search).map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                onNodeSelect={handleNodeSelect}
                selectedId={selectedNode?.id}
                isDarkMode={isDarkMode}
              />
            ))}

            {/* Empty state */}
            {filterTree(normalizedTree, search).length === 0 && (
              <Typography sx={{ textAlign: "center", mt: 2, color: "text.secondary", fontFamily: "inherit", fontSize: 13 }}>
                No results found
              </Typography>
            )}
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
}